import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalWagerTabs } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  handleInfoClear,
  getWagerItem,
  handleUpdateFields,
  deleteWagerItem,
  createWager,
  updateWager,
  removeWagerGames,
} from "store/liveOps/wagers/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { GameItem } from "store/gameCenter/games/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { wagersListLimits } from "configs/limits";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import Pagination from "components/Pagination";
import { Modal } from "components/Modal/Modal";
import noImage from "assets/images/image_not_found.webp";
import styles from "./LiveOpsWagerGamesEditModal.module.scss";
import { Options } from "components/table/components/Options/Options";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";

const LiveOpsWagerGamesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const itemFetched = useAppSelector((store) => store.wagers.isItemFetched);
  const itemPayload = useAppSelector((store) => store.wagers.payload);
  const isItemFetching = useAppSelector((store) => store.wagers.isItemFetching);

  const [tabs] = useState<ModalWagerTabs[]>([
    ModalWagerTabs.info,
    ModalWagerTabs.games,
    // ModalWagerTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalWagerTabs>(
    ModalWagerTabs.info,
  );
  const [deleteValue, setDeleteValue] = useState<string>("");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] =
    useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [gamesList, setGamesList] = useState<GameItem[]>([]);
  const [currentLimit, setLimit] = useState<number>(wagersListLimits[0]);
  const [isGamesLoading, setIsGamesLoading] = useState(false);
  const formErrors = useFormErrors();

  const handleLoadGames = async () => {
    setIsGamesLoading(true);

    try {
      const result = await instance.get(
        `${apiRoutes.wagerListGames}/${searchParams.get("entityId")}?limit=${currentLimit}&offset=${activePage - 1}&gameTitle=${encodeURIComponent(debouncedSearchValue)}`,
      );

      setGamesList(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch {
      console.log("handleLoadGames error");
    } finally {
      setIsGamesLoading(false);
    }
  };

  const permissions = usePermissionHelper();
  const canSave = searchParams.get("entityId")
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setActivePage(1);
      setDebouncedSearchValue(searchValue.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    handleLoadGames();
  }, [debouncedSearchValue, activePage, currentLimit]);

  useEffect(() => {
    if (searchParams.get("entityId") !== null) {
      if (searchParams.get("tab") !== null) {
        setActiveTab(searchParams.get("tab") as ModalWagerTabs);
      }
      dispatch(
        getWagerItem(parseInt(searchParams.get("entityId") as string, 10)),
      );
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  }, [searchParams]);

  const isLoaded = () => {
    if (!itemFetched && searchParams.get("entityId") !== null) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleTabClick = (tab: ModalWagerTabs) => {
    if (!isEdit && tab !== ModalWagerTabs.info) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };
  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleInfoClear());
  };

  const handleData = (key: string, value: string | boolean | number) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: value }));
  };

  const handleGetPayload = () => {
    return itemPayload?.title;
  };

  const handleDeleteClick = async () => {
    const result = await dispatch(deleteWagerItem(itemPayload?.id ?? 0));
    if (result.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
    handleCloseClick();
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleDeleteGame = async (gameId: number) => {
    await dispatch(
      removeWagerGames({
        wagerId: Number(searchParams.get("entityId")),
        gameIds: [gameId],
      }),
    );
    handleLoadGames();
    dispatch(
      showNotification({
        title: t("success"),
        message: t("entityDeleteSuccess"),
        type: NotificationTypes.success,
      }),
    );
  };

  const renderTab = () => {
    if (activeTab === ModalWagerTabs.info) {
      return (
        <div className="wager-form-wrapper">
          <div className="wager-form-item">
            <InnerInput
              value={itemPayload?.title ?? ""}
              placeholder={t("title")}
              label={t("title")}
              type="text"
              id="wager-title"
              onChange={(e) => {
                handleData("title", e);
                formErrors.clearFieldError("title");
              }}
              hasError={Boolean(formErrors.errors["title"])}
              disabled={!canSave}
            />
          </div>
          <div className="wager-form-item">
            <InnerInput
              value={itemPayload?.contribution?.toString() ?? ""}
              placeholder={t("contribution")}
              label={t("contribution")}
              type="text"
              id="wager-contribution"
              onChange={(e) => {
                handleData("contribution", e);
                formErrors.clearFieldError("contribution");
              }}
              hasError={Boolean(formErrors.errors["contribution"])}
              disabled={!canSave}
            />
          </div>
          <div className="wager-form-item">
            <InnerInput
              maxCodePoints={2500}
              value={itemPayload?.description ?? ""}
              placeholder={t("enterText")}
              label={t("description")}
              type="textarea"
              id="game-description"
              onChange={(e) => {
                handleData("description", e);
                formErrors.clearFieldError("description");
              }}
              hasError={Boolean(formErrors.errors["description"])}
              disabled={!canSave}
            />
          </div>
          <div className="wager-form-item">
            <SwitchBtn
              activeTxt={t("active")}
              isActive={itemPayload?.isActive}
              handleClick={() => {
                handleData("isActive", !!itemPayload?.isActive ? 0 : 1);
                formErrors.clearFieldError("isActive");
              }}
              hasError={Boolean(formErrors.errors.isActive)}
              disabled={!canSave}
            />
          </div>
        </div>
      );
    }
    if (activeTab === ModalWagerTabs.games) {
      return (
        <div className={`wager-list-games ${styles.gamesTab}`}>
          <div className="games-order-search">
            <SearchInput
              placeholder={t("searchGame")}
              value={searchValue}
              onChange={onSearch}
              isSmall
            />
          </div>
          <div className={`games-order-list ${styles.gamesTable}`}>
            <div className="item">
              <div className={styles.gameIdColumn}>ID</div>
              <div className={styles.gameImageColumn}>{t("image")}</div>
              <div className={styles.gameTitleColumn}>{t("game")}</div>
              <div className={styles.gameProviderColumn}>{t("provider")}</div>
              {permissions?.update && (
                <div className={styles.gameOptionsColumn}>{t("options")}</div>
              )}
            </div>

            {isGamesLoading ? (
              <ModalTabSkeleton />
            ) : (
              <>
                {gamesList.map((el) => (
                  <div className="item" key={el.id}>
                    <div className={styles.gameIdColumn}>{el.id}</div>
                    <div className={styles.gameImageColumn}>
                      <img
                        src={(el.image as string) || noImage}
                        alt=""
                        className="game-img"
                        onError={(event) => {
                          event.currentTarget.src = noImage;
                          event.currentTarget.onerror = null;
                        }}
                      />
                    </div>
                    <div className={styles.gameTitleColumn}>
                      <CopyToClipboard text={el.title} />
                      <span>{el.title}</span>
                    </div>
                    <div className={styles.gameProviderColumn}>
                      {el.provider.title}
                    </div>
                    {permissions?.update && (
                      <div className={styles.gameOptionsColumn}>
                        <Options
                          id={el.id}
                          hasDelete={true}
                          hasEdit={false}
                          instantDelete={true}
                          onDelete={() => handleDeleteGame(el.id)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              setActivePage(1);
            }}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={wagersListLimits}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </div>
      );
    }

    if (activeTab === ModalWagerTabs.settings) {
      return (
        <div className="game-form-wrapper">
          <div className="game-form-content delete">
            <div className="game-form-box">
              <div className="game-form-box--item">
                <InnerInput
                  value={deleteValue}
                  placeholder={t("enterEntityTitleToDelete")}
                  label={t("areYouSureYouWantDeleteThisEntity")}
                  type="text"
                  id="game-title"
                  onChange={(e) => setDeleteValue(e)}
                />
                <Button
                  isDelete
                  txt={t("delete")}
                  handleClick={handleDeleteClick}
                  disabled={deleteValue !== handleGetPayload()}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <div className="game-form-wrapper"></div>;
  };

  const handleApply = async () => {
    if (!canSave) {
      return;
    }

    if (!itemPayload) return;

    formErrors.resetErrors();

    try {
      if (isEdit) {
        await dispatch(updateWager(itemPayload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const result = await dispatch(createWager(itemPayload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        navigate(`${location.pathname}?activeModal=wager&entityId=${result}`);
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage(activePage - 1);
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      setActivePage(activePage + 1);
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("wagerGame")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={
              activeTab === ModalWagerTabs.games ? styles.gamesContent : undefined
            }
          >
            {renderTab()}
          </Modal.Content>
          {canSave && activeTab !== ModalWagerTabs.games && (
            <Modal.Footer>
              <Button
                isSecondary
                txt={t("cancel")}
                handleClick={handleCloseClick}
              />

              <Button
                isPrimary
                handleClick={handleApply}
                txt={t("save")}
                isLoad={isItemFetching}
              />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsWagerGamesEditModal;
